import React, { useState } from "react";
import { Download, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Container } from "@/components/primitives";
import { submitResourceRequest, API } from "@/lib/api";
import { useDocumentMeta } from "@/lib/meta";

const RESOURCES = [
  {
    id: "the-why",
    eyebrow: "2-page pitch",
    title: "The Why",
    blurb:
      "Your roadmap, not a vendor's. The owner's case for CaseMinder + ComplianceMinder — built around how your firm actually works, for the fee earner and the board.",
    file: "the-why/light.pdf",
  },
  {
    id: "one-pager",
    eyebrow: "One-pager",
    title: "ComplianceMinder + CaseMinder — the ROI",
    blurb:
      "The punchiest reasons owner-managed firms move to Beyond the Box: own your compliance, own your operations, on the Microsoft 365 you already control.",
    file: "one-pager/light.pdf",
  },
];

export default function Resources() {
  useDocumentMeta({
    title: "Resources",
    description:
      "Download the Beyond the Box pitch and ROI one-pager for owner-managed law firms — CaseMinder and ComplianceMinder on your own Microsoft 365.",
    path: "/resources",
  });

  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", firm: "", email: "" });
  const [busy, setBusy] = useState(false);

  const openGate = (r) => {
    setForm({ name: "", firm: "", email: "" });
    setActive(r);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitResourceRequest({ ...form, resource: active.title });
      toast.success("Thanks — your download is starting.");
      window.location.href = `${API}/${active.file}`;
      setActive(null);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="resources-page">
      {/* Hero */}
      <section className="border-b border-border py-24 md:py-32">
        <Container>
          <div className="max-w-3xl">
            <span className="overline text-accent">Resources</span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground sm:text-5xl lg:text-6xl">
              The case, on paper.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Short, sharp collateral to share with partners, the board and your IT team. Tell us where
              to credit it and the download starts straight away.
            </p>
          </div>
        </Container>
      </section>

      {/* Resource grid */}
      <section className="py-20 md:py-24">
        <Container>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {RESOURCES.map((r) => (
              <article
                key={r.id}
                data-testid={`resource-card-${r.id}`}
                className="flex flex-col border border-border p-8 transition-colors hover:border-accent"
              >
                <span className="overline text-accent">{r.eyebrow}</span>
                <h2 className="mt-4 font-display text-2xl font-bold tracking-[-0.02em] text-foreground">
                  {r.title}
                </h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{r.blurb}</p>
                <button
                  type="button"
                  data-testid={`resource-download-${r.id}`}
                  onClick={() => openGate(r)}
                  className="mt-6 inline-flex items-center gap-2 self-start bg-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  <Download strokeWidth={2} className="h-4 w-4" /> Download PDF
                </button>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* Gate modal */}
      {active && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-6"
          data-testid="resource-gate-modal"
          onClick={() => setActive(null)}
        >
          <div
            className="relative w-full max-w-md border border-border bg-background p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="resource-gate-close"
              onClick={() => setActive(null)}
              className="absolute right-4 top-4 text-muted-foreground transition-colors hover:text-accent"
              aria-label="Close"
            >
              <X strokeWidth={1.8} className="h-5 w-5" />
            </button>
            <span className="overline text-accent">Download</span>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-[-0.02em] text-foreground">
              {active.title}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">A couple of details and it's yours.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="resource-gate-form">
              <input
                required
                data-testid="resource-input-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className="w-full border border-border bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none"
              />
              <input
                required
                data-testid="resource-input-firm"
                value={form.firm}
                onChange={set("firm")}
                placeholder="Firm"
                className="w-full border border-border bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none"
              />
              <input
                required
                type="email"
                data-testid="resource-input-email"
                value={form.email}
                onChange={set("email")}
                placeholder="Work email"
                className="w-full border border-border bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="resource-gate-submit"
                className="inline-flex w-full items-center justify-center gap-2 bg-primary px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
              >
                {busy ? "Preparing…" : "Get the download"} <ArrowRight strokeWidth={2} className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
